<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { InvitationSettlementRow } from '@/api/types/domain'
import {
  addInvitation,
  deleteInvitation,
  fetchInvitationSettlement,
} from '@/api/modules/invitations'

const filters = reactive({ keyword: '', start: '' as string, end: '' as string })
const q = reactive({ pageUi: 1, size: 25 })
const loading = ref(false)
const rows = ref<InvitationSettlementRow[]>([])
const total = ref(0)

async function load() {
  loading.value = true
  try {
    const { data } = await fetchInvitationSettlement({
      ...(filters.keyword?.trim() ? { keyword: filters.keyword.trim() } : {}),
      ...(filters.start ? { start: filters.start } : {}),
      ...(filters.end ? { end: filters.end } : {}),
      page: Math.max(0, q.pageUi - 1),
      size: q.size,
    })
    rows.value = data.content ?? []
    total.value = data.totalElements ?? 0
  } finally {
    loading.value = false
  }
}

onMounted(load)

function search() {
  q.pageUi = 1
  load()
}

function onPage() {
  load()
}

function onSize(s: number) {
  q.size = s
  q.pageUi = 1
  load()
}

async function onAddSalesStaff() {
  try {
    const { value } = await ElMessageBox.prompt('Salesperson name', 'Add sales staff', {
      inputPattern: /\S+/,
      inputErrorMessage: 'Salesperson name cannot be empty',
    })
    const name = String(value ?? '').trim()
    const { data } = await addInvitation(name)
    if (data?.status !== undefined && data.status !== 1) {
      ElMessage.error(data.msg || 'failed')
      return
    }
    ElMessage.success(data?.msg ?? 'success')
    load()
  } catch {
    /* cancel */
  }
}

function confirmDelete(row: InvitationSettlementRow) {
  const sid = row.salesperson ?? String(row.id)
  ElMessageBox.confirm(`Delete salesperson “${sid}” and their invitation row?`, 'Confirm', {
    confirmButtonText: 'Delete',
    cancelButtonText: 'Cancel',
    type: 'warning',
  }).then(async () => {
    const { data } = await deleteInvitation(row.id)
    if (data?.status !== 1) {
      ElMessage.error(data?.msg ?? 'failed')
      return
    }
    ElMessage.success('Deleted')
    load()
  })
}
</script>

<template>
  <PageChrome title="Invitation code settlement" description="邀约统计：可按业务员关键字与注册日期范围筛选。">
    <template #actions>
      <el-button type="danger" plain @click="onAddSalesStaff">Add sales staff</el-button>
      <el-button :loading="loading" @click="load">Refresh</el-button>
    </template>

    <el-alert type="info" show-icon class="inv-alert" :closable="false">
      Date range filters registrations by signup time (<code>user.addtime</code>). The statistics column counts users
      with matching invitation codes in that window; zero means none.
    </el-alert>

    <div class="ep-filter-panel">
      <el-form inline label-width="100px">
        <el-form-item label="业务员">
          <el-input v-model="filters.keyword" clearable placeholder="keyword" style="width: 200px" />
        </el-form-item>
        <el-form-item label="From">
          <el-date-picker
            v-model="filters.start"
            type="date"
            placeholder="start"
            value-format="YYYY-MM-DD"
            style="width: 160px"
            clearable
          />
        </el-form-item>
        <el-form-item label="To">
          <el-date-picker
            v-model="filters.end"
            type="date"
            placeholder="end"
            value-format="YYYY-MM-DD"
            style="width: 160px"
            clearable
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="search">Filter</el-button>
        </el-form-item>
      </el-form>
    </div>

    <el-card shadow="hover" class="ep-table-scope">
      <el-table v-loading="loading" :data="rows" stripe border>
        <el-table-column prop="id" label="ID" width="76" align="center" />
        <el-table-column prop="salesperson" label="Salesman" min-width="140" show-overflow-tooltip />
        <el-table-column prop="invitationCode" label="Invitation code" min-width="120" />
        <el-table-column prop="statistic" label="Statistics" width="110" align="center" />
        <el-table-column prop="addtime" label="Created" min-width="165" />
        <el-table-column label="Operate" width="112" fixed="right" align="center">
          <template #default="{ row }">
            <el-button link type="danger" @click="confirmDelete(row)">Delete</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="ep-pager-footer">
        <el-pagination
          v-model:current-page="q.pageUi"
          v-model:page-size="q.size"
          :total="total"
          :page-sizes="[10, 15, 25, 50]"
          layout="sizes,prev,pager,next,total"
          background
          @current-change="onPage"
          @size-change="onSize"
        />
      </div>
    </el-card>
  </PageChrome>
</template>

<style scoped lang="scss">
.inv-alert {
  margin-bottom: 18px;
}
</style>
